// ./src/components/features/Themes/ThemeFormModal/themeConfigUtils.ts
import { defaultThemeConfig } from "@/config";

/**
 * Parse theme config string to object
 * Returns defaultThemeConfig if string is empty, null, or invalid JSON
 */
export const parseThemeConfig = (
  themeConfigString: string | null | undefined
): any => {
  // Handle empty or null cases
  if (!themeConfigString || themeConfigString.trim() === "") {
    return { ...defaultThemeConfig };
  }

  try {
    const parsed = JSON.parse(themeConfigString);

    // Ensure we got an object
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      console.warn("Invalid theme config structure, using defaults");
      return { ...defaultThemeConfig };
    }
    return parsed;
  } catch (error) {
    console.warn("Failed to parse theme config, using defaults:", error);
    return { ...defaultThemeConfig };
  }
};

/**
 * Get nested value from object using dot notation path
 * Example: getNestedValue(obj, "global.color") returns obj.global.color
 */
export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  if (!path || !obj) {
    return undefined;
  }

  const keys = path.split(".");
  let current = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (typeof current !== "object") {
      return undefined;
    }

    current = current[key];
  }

  return current;
};

/**
 * Set nested value in object using dot notation path
 * Creates intermediate objects if they don't exist
 * Returns a new object (immutable update)
 * Example: setNestedValue(obj, "global.color", "#ffffff")
 */
export const setNestedValue = (
  obj: Record<string, any>,
  path: string,
  value: any
): Record<string, any> => {
  if (!path) {
    return obj;
  }

  // Create a deep clone to avoid mutations
  const newObj = JSON.parse(JSON.stringify(obj));

  const keys = path.split(".");
  let current = newObj;

  // Navigate to the parent of the target property
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];

    // Create intermediate object if it doesn't exist
    if (!current[key] || typeof current[key] !== "object") {
      current[key] = {};
    }

    current = current[key];
  }

  // Set the value
  const lastKey = keys[keys.length - 1];
  current[lastKey] = value;

  return newObj;
};

/**
 * Stringify theme config object for API
 * Returns null if object is empty or invalid
 */
export const stringifyThemeConfig = (
  configObj: Record<string, any> | null | undefined
): string | null => {
  if (!configObj || typeof configObj !== "object") {
    return null;
  }

  try {
    return JSON.stringify(configObj);
  } catch (error) {
    console.error("Failed to stringify theme config:", error);
    return null;
  }
};

/**
 * Validate that all required paths exist in config
 * Returns true if valid, false otherwise
 */
export const validateThemeConfig = (
  configObj: Record<string, any>,
  requiredPaths: string[]
): boolean => {
  for (const path of requiredPaths) {
    const value = getNestedValue(configObj, path);
    if (value === undefined || value === null) {
      return false;
    }
  }
  return true;
};

/**
 * Merge partial config with defaults
 * Useful for ensuring all required fields exist
 */
export const mergeWithDefaults = (
  partialConfig: Record<string, any>
): Record<string, any> => {
  const merged = { ...defaultThemeConfig };

  const deepMerge = (target: any, source: any) => {
    for (const key in source) {
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        target[key] = target[key] || {};
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  };

  deepMerge(merged, partialConfig);
  return merged;
};
